import dayjs from 'dayjs';
import store from '../redux/store';
import { CartItem } from '../types/cart';
import { DataLayerEventName, DataLayerEventType } from '../types/data-layer';
import { dataLayerPush } from '@equipmentshare/omnichannel-datalayer';
import { debounce } from 'lodash-es';
import { getAllMessageSettings } from '@app/apis/profile';
import { Identity } from '@equipmentshare/auth0-react';
import { MessageTypeGroups, MessageTypes } from '@app/types/profile';
import { PriceEntry } from '../types/equipment';
import { Quote, QuoteEquipmentItem } from '../types/quotes';
import {
  Checkout,
  ContactDetails,
  DeliveryPickupDetails,
  JobsiteDetails,
  Product,
} from '@equipmentshare/omnichannel-datalayer/dist/Events/components';
import {
  CartViewedEvent,
  CheckoutStartedEvent,
  CheckoutStepCompletedWithDeliveryEvent,
  CheckoutStepCompletedWithPickupEvent,
  CheckoutStepViewedEvent,
  PriceUpdatedEvent,
  ProductAddedEvent,
  QuoteRequestedEvent,
  SignInEvent,
  SignUpEvent,
  MarketingEmailOptInEvent,
} from '@equipmentshare/omnichannel-datalayer/dist/Events';

const debouncedEvents = {
  priceUpdated: debounce((total: number | undefined) => priceUpdatedEvent(total), 2000),
  checkoutStepViewed: debounce(async () => await checkoutStepViewedEvent(), 2000),
  cartViewed: debounce(() => viewCartEvent(), 2000),
  checkoutStarted: debounce(async () => await checkoutStartedEvent(), 2000),
  checkoutStepCompleted: debounce(async (callback?: () => void) => {
    await triggerCheckoutStepCompleteEvent();
    if (callback) callback();
  }, 2000),
  signUp: debounce(
    (
      companyId: string,
      userId: string,
      firstName: string,
      lastName: string,
      companyName: string | undefined,
      email: string | undefined,
    ) => signUpEvent(companyId, userId, firstName, lastName, companyName, email),
    2000,
  ),
  signIn: debounce(() => signInEvent(), 2000),
  marketingEmailOptIn: debounce(
    (companyId?: number, userId?: number, marketing_email_opt_in?: boolean) =>
      marketingEmailOptIn(companyId, userId, marketing_email_opt_in),
    2000,
  ),
};

export const pushEventToDataLayer = async (event: DataLayerEventType) => {
  try {
    switch (event.type) {
      case DataLayerEventName.PRODUCT_ADDED:
        await pushProductAddedEvent(event.payload.cartItem);
        break;
      case DataLayerEventName.CART_VIEWED:
        debouncedEvents.cartViewed();
        break;
      case DataLayerEventName.CHECKOUT_STARTED:
        debouncedEvents.checkoutStarted();
        break;
      case DataLayerEventName.CHECKOUT_STEP_VIEWED:
        await debouncedEvents.checkoutStepViewed();
        break;
      case DataLayerEventName.CHECKOUT_STEP_COMPLETED:
        await debouncedEvents.checkoutStepCompleted(() => {
          debouncedEvents.checkoutStepViewed();
        });
        break;
      case DataLayerEventName.PRICE_UPDATED:
        debouncedEvents.priceUpdated(event.payload.total);
        break;
      case DataLayerEventName.QUOTE_REQUESTED:
        await quoteRequestedEvent(event.payload.quote_id);
        break;
      case DataLayerEventName.SIGN_UP:
        debouncedEvents.signUp(
          String(event.payload.company_id),
          String(event.payload.user_id),
          String(event.payload.first_name),
          String(event.payload.last_name),
          String(event.payload.company_name),
          String(event.payload.email),
        );
        break;
      case DataLayerEventName.SIGN_IN:
        debouncedEvents.signIn();
        break;
      case DataLayerEventName.MARKETINT_EMAIL_OPT_IN:
        debouncedEvents.marketingEmailOptIn(
          event?.payload?.company_id,
          event?.payload?.user_id,
          event?.payload?.marketing_email_opt_in,
        );
        break;
    }
  } catch (error) {
    console.error('Error occurred in pushEventToDataLayer:', error);
  }
};

const waitForQuoteToLoad = async (
  maxTries = 10,
  delayMs = 500,
): Promise<Quote | null | undefined> => {
  for (let attempt = 1; attempt <= maxTries; attempt++) {
    const cart = store.getState().cart;
    if (!cart?.loadingFlags?.isLoadingQuote && cart?.backendSyncStatus != 'IS_LOADING') {
      return cart?.quote;
    }

    console.debug(`Attempt ${attempt}/${maxTries}: Waiting for quote data...`);
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  console.error('Quote data not available after max retries.');
  return null;
};

const waitForAuthToLoad = async (maxTries = 10, delayMs = 500): Promise<Identity | null> => {
  for (let attempt = 1; attempt <= maxTries; attempt++) {
    const auth = store.getState().auth;
    if (auth?.authContext) {
      return auth?.authContext;
    }

    console.debug(`Attempt ${attempt}/${maxTries}: Waiting for auth data...`);
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  console.error('Auth data not available after max retries.');
  return null;
};

const waitForCartToLoad = async (
  maxTries = 10,
  delayMs = 500,
): Promise<string | null | undefined> => {
  for (let attempt = 1; attempt <= maxTries; attempt++) {
    const cart = store.getState().cart;
    if (cart?.userUuid) {
      return cart?.userUuid;
    }

    console.debug(`Attempt ${attempt}/${maxTries}: Waiting for cart data...`);
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  console.error('Cart data not available after max retries.');
  return null;
};

const rentalDurationDays = (startDate: number, endDate: number): number => {
  if (!startDate || !endDate) return 0;
  const start = dayjs.unix(startDate);
  const end = dayjs.unix(endDate);
  return end.diff(start, 'day') + 1;
};

const getCheckoutFormStepName = () => {
  const checkoutStepper = store.getState().checkout.stepper;

  if (checkoutStepper['deliveryDetails'].isComplete) {
    return;
  } else if (checkoutStepper['jobsiteDetails'].isComplete) {
    return 'Delivery & Pickup';
  } else {
    return 'Jobsite';
  }
};

const getPriceEventStepName = () => {
  if (window.location.pathname.includes('/cart')) {
    return 'Cart';
  } else if (window.location.pathname.includes('/checkout')) {
    return getCheckoutFormStepName() ?? 'Delivery & Pickup';
  } else {
    return 'Non Cart';
  }
};

export const triggerCheckoutStepCompleteEvent = async () => {
  const checkoutStepper = store.getState().checkout.stepper;

  if (checkoutStepper['deliveryDetails'].isComplete) {
    await checkoutCompleteWithDeliveryEvent();
  } else if (checkoutStepper['jobsiteDetails'].isComplete) {
    await checkoutCompleteWithJobsiteEvent();
  } else {
    return;
  }
};

const getJobsiteDetails = (): JobsiteDetails => {
  const jobsiteDetails = store.getState().checkout.stepper['jobsiteDetails'].formData;
  const [lat = '', lon = ''] = jobsiteDetails.latitudeLongitude?.split(', ') ?? [];

  return {
    jobsite_name: jobsiteDetails?.jobsiteName,
    jobsite_address: jobsiteDetails?.address,
    jobsite_lat: lat,
    jobsite_lon: lon,
    zip_code: jobsiteDetails?.zip,
    city: jobsiteDetails?.city,
    state: jobsiteDetails?.state,
    save_jobsite: jobsiteDetails?.saveNewJobsite ?? false,
  };
};

const getCommonContactDetails = async (): Promise<Omit<ContactDetails, 'mobile_number'>> => {
  const auth = await waitForAuthToLoad();

  const jobsiteDetails = store.getState().checkout.stepper['jobsiteDetails'].formData;
  const fullName = auth?.fullName ?? '';
  const nameParts = fullName.trim() ? fullName.split(' ') : [];

  return {
    ...(auth?.userId ? { user_id: String(auth.userId) } : {}),
    ...(auth?.companyId ? { company_id: String(auth.companyId) } : {}),
    company_name: auth?.companyName ?? jobsiteDetails?.company ?? null,
    email: auth?.email ?? jobsiteDetails?.email ?? null,
    first_name: jobsiteDetails?.firstName ?? nameParts[0] ?? '',
    last_name: jobsiteDetails?.lastName ?? nameParts.slice(1).join(' ') ?? '',
  };
};

const getContactDetailsWithMobile = async (): Promise<ContactDetails> => ({
  ...(await getCommonContactDetails()),
  mobile_number: store.getState().checkout?.stepper['jobsiteDetails']?.formData?.mobile ?? '',
});

const getContactDetailsWithoutMobile = async (): Promise<Omit<ContactDetails, 'mobile_number'>> =>
  getCommonContactDetails();

const getDeliveryDetails = (): DeliveryPickupDetails => {
  const deliveryDetails = store.getState().checkout.stepper['deliveryDetails'].formData;

  return {
    delivery_method:
      deliveryDetails?.modeOfDelivery == 'SELF' ? 'I’ll pick up' : 'Delivery to jobsite',
    return_method: deliveryDetails?.modeOfReturning == 'SELF' ? 'I’ll drop off' : 'Pick up for me',
    receiver: deliveryDetails?.contactName,
    purchase_order: deliveryDetails?.purchaseOrder,
    special_instructions: deliveryDetails?.specialRequests_DeliveryInstructions,
  };
};

const createProduct = (
  cartItem: CartItem,
  equipmentPrice: PriceEntry,
  equipmentQuote?: QuoteEquipmentItem,
): Product => {
  return {
    id: String(cartItem.equipmentId ?? ''),
    equipment_class: cartItem?.name,
    equipment_class_id: String(cartItem?.equipmentId ?? ''),
    category: cartItem?.categoryName ?? '',
    category_id: String(cartItem?.categoryId ?? ''),
    categories: cartItem?.categoryNameHierarchy ?? [],
    category_ids: cartItem?.categoryIdHierarchy ?? [],
    quantity: cartItem?.quantity,
    rate_1_day: equipmentPrice?.price?.price_per_day ?? 0,
    rate_7_day: equipmentPrice?.price?.price_per_week ?? 0,
    rate_28_day: equipmentPrice?.price?.price_per_month ?? 0,
    ...(cartItem.startDate && {
      rental_start_date: dayjs.unix(cartItem.startDate).format('YYYY-MM-DD'),
    }),
    image_source: cartItem.imageSource ?? '',
    ...(cartItem.endDate && { rental_end_date: dayjs.unix(cartItem.endDate).format('YYYY-MM-DD') }),
    rental_duration_days: rentalDurationDays(cartItem.startDate, cartItem.endDate),
    extended_subtotal: Number(equipmentQuote?.total) ?? 0,
    market_id: String(equipmentPrice?.branchId ?? ''),
    ...(equipmentPrice?.companyId
      ? { negotiated_rate_company_id: String(equipmentPrice.companyId) }
      : {}),
  };
};

const getCartProducts = (): Array<Product> => {
  const cart = store.getState().cart;
  const prices = store.getState().prices;

  const products: Array<Product> = [];
  cart.cartItems.map((cartItem) => {
    const equipmentPrice = prices.priceCatalog[cartItem.equipmentId];
    const equipmentQuote = cart.quote?.rental_items_summary.equipment_types?.filter(
      (eq) => eq.equipment_class_id == cartItem.equipmentId,
    )[0];

    products.push(createProduct(cartItem, equipmentPrice, equipmentQuote));
  });

  return products;
};

const getCommonCartFields = (): Pick<
  CartViewedEvent,
  'cart_id' | 'cart_product_count' | 'cart_extended_subtotal' | 'currency'
> => {
  const cart = store.getState().cart;

  return {
    cart_id: cart.userUuid ?? 'cart_id',
    cart_product_count: Number(cart.cartItems?.length) || 0,
    cart_extended_subtotal: Number(cart.quote?.total) ?? 0,
    currency: 'USD',
  };
};

const commonRentalCheckoutData = (
  quote: Quote,
): Pick<
  Checkout,
  | 'equipment_charges'
  | 'rental_protection_plan_fee'
  | 'total'
  | 'taxes'
  | 'rental_protection_plan'
  | 'equipment_daily_usage_rate'
  | 'delivery_fee'
> => {
  const cart = store.getState().cart;

  return {
    equipment_charges: Number(quote?.equipment_charges) || 0,
    rental_protection_plan_fee: Number(quote?.rpp) || 0,
    total: Number(quote?.total) || 0,
    taxes: Number(quote?.combined_sales_tax) || 0,
    rental_protection_plan:
      cart.rentalProtection?.active?.type === 'STANDARD'
        ? 'Standard Rental Agreement'
        : 'My own rental insurance',
    equipment_daily_usage_rate: Number(cart.shift?.active?.plan?.multiplier) || 1,
    delivery_fee: Number(quote?.total_delivery_fee) || 0,
  };
};

// Push to cart event
const pushProductAddedEvent = async (cartItem: CartItem) => {
  const quoteData = await waitForQuoteToLoad();
  if (!quoteData) {
    return;
  }

  const userUuid = await waitForCartToLoad();
  if (!userUuid) {
    return;
  }

  const prices = store.getState().prices;

  const equipmentPrice = prices?.priceCatalog[cartItem.equipmentId];
  const equipmentQuote = quoteData?.rental_items_summary?.equipment_types?.filter(
    (eq) => eq.equipment_class_id == cartItem.equipmentId,
  )[0];

  try {
    const eventPayload: ProductAddedEvent = {
      event: DataLayerEventName.PRODUCT_ADDED,
      ...getCommonCartFields(),
      products: [createProduct(cartItem, equipmentPrice, equipmentQuote)],
    };

    dataLayerPush(eventPayload);
  } catch (error) {
    console.error('Error occurred in pushProductAddedEvent:', error);
  }
};

// View cart event
export const viewCartEvent = async () => {
  const quoteData = await waitForQuoteToLoad();
  if (!quoteData) {
    return;
  }

  try {
    const eventPayload: CartViewedEvent = {
      event: DataLayerEventName.CART_VIEWED,
      ...getCommonCartFields(),
      products: getCartProducts(),
    };

    dataLayerPush(eventPayload);
  } catch (error) {
    console.error('Error occurred in viewCartEvent:', error);
  }
};

// Checkout start event
export const checkoutStartedEvent = async () => {
  const quoteData = await waitForQuoteToLoad();
  if (!quoteData) {
    return;
  }

  try {
    const eventPayload: CheckoutStartedEvent = {
      event: DataLayerEventName.CHECKOUT_STARTED,
      ...getCommonCartFields(),
      ...commonRentalCheckoutData(quoteData),
      products: getCartProducts(),
      rental_charges: Number(quoteData?.rental_subtotal) ?? 0,
    };

    dataLayerPush(eventPayload);
  } catch (error) {
    console.error('Error occurred in checkoutStartedEvent:', error);
  }
};

export const checkoutStepViewedEvent = async () => {
  const quoteData = await waitForQuoteToLoad();
  if (!quoteData) {
    return;
  }

  const eventStepName = getCheckoutFormStepName();
  if (!eventStepName) {
    return;
  }

  try {
    const eventPayload: CheckoutStepViewedEvent = {
      event: DataLayerEventName.CHECKOUT_STEP_VIEWED,
      step_name: eventStepName,
      ...commonRentalCheckoutData(quoteData),
      ...getCommonCartFields(),
      products: getCartProducts(),
      rental_charges: Number(quoteData?.rental_subtotal) ?? 0,
    };

    dataLayerPush(eventPayload);
  } catch (error) {
    console.error('Error occurred in checkoutStepViewedEvent:', error);
  }
};

export const priceUpdatedEvent = async (total: number | undefined) => {
  const quoteData = await waitForQuoteToLoad();
  if (!quoteData) {
    return;
  }

  const eventStepName = getPriceEventStepName();
  if (!eventStepName) {
    return;
  }

  const products = getCartProducts();
  if (products.length == 0) {
    return;
  }

  try {
    const eventPayload: PriceUpdatedEvent = {
      event: DataLayerEventName.PRICE_UPDATED,
      step_name: eventStepName,
      ...getCommonCartFields(),
      ...commonRentalCheckoutData(quoteData),
      products: getCartProducts(),
      rental_charges: Number(quoteData?.rental_subtotal) ?? 0,
      rental_protection_plan_fee: Number(quoteData?.rpp) ?? 0,
    };

    if (!total || Number(total) == eventPayload.total) {
      return;
    }

    dataLayerPush(eventPayload);
  } catch (error) {
    console.error('Error occurred in priceUpdatedEvent:', error);
  }
};

export const checkoutCompleteWithJobsiteEvent = async () => {
  const quoteData = await waitForQuoteToLoad();
  if (!quoteData) {
    return;
  }

  try {
    const eventPayload: CheckoutStepCompletedWithPickupEvent = {
      event: DataLayerEventName.CHECKOUT_STEP_COMPLETED,
      step_name: 'Jobsite',
      ...getCommonCartFields(),
      ...commonRentalCheckoutData(quoteData),
      jobsite_details: getJobsiteDetails(),
      contact_details: await getContactDetailsWithMobile(),
      products: getCartProducts(),
      rental_charges: Number(quoteData?.rental_subtotal) ?? 0,
    };

    dataLayerPush(eventPayload);
  } catch (error) {
    console.error('Error occurred in checkoutCompleteWithJobsiteEvent:', error);
  }
};

export const checkoutCompleteWithDeliveryEvent = async () => {
  const quoteData = await waitForQuoteToLoad();
  if (!quoteData) {
    return;
  }

  try {
    const eventPayload: CheckoutStepCompletedWithDeliveryEvent = {
      event: DataLayerEventName.CHECKOUT_STEP_COMPLETED,
      step_name: 'Delivery & Pickup',
      ...getCommonCartFields(),
      ...commonRentalCheckoutData(quoteData),
      delivery_pickup_details: getDeliveryDetails(),
      products: getCartProducts(),
      rental_charges: Number(quoteData?.rental_subtotal) ?? 0,
    };

    dataLayerPush(eventPayload);
  } catch (error) {
    console.error('Error occurred in checkoutCompleteWithDeliveryEvent:', error);
  }
};

export const quoteRequestedEvent = async (quote_id: string) => {
  const quoteData = await waitForQuoteToLoad();
  if (!quoteData) {
    return;
  }

  try {
    const eventPayload: QuoteRequestedEvent = {
      event: DataLayerEventName.QUOTE_REQUESTED,
      ...getCommonCartFields(),
      ...commonRentalCheckoutData(quoteData),
      contact_details: await getContactDetailsWithMobile(),
      jobsite_details: getJobsiteDetails(),
      delivery_pickup_details: getDeliveryDetails(),
      products: getCartProducts(),
      quote_id: quote_id,
      tax: Number(quoteData?.combined_sales_tax) ?? 0,
      rental_charges: Number(quoteData?.rental_items_summary?.total) ?? 0,
    };

    dataLayerPush(eventPayload);
  } catch (error) {
    console.error('Error occurred in quoteRequestedEvent:', error);
  }
};

export const signUpEvent = async (
  company_id: string,
  user_id: string,
  firstName: string,
  lastName: string,
  companyName: string | undefined,
  email: string | undefined,
) => {
  try {
    const eventPayload: SignUpEvent = {
      event: DataLayerEventName.SIGN_UP,
      user_id,
      first_name: firstName,
      last_name: lastName,
      company_name: companyName ?? '',
      company_id,
      email: email ?? '',
      source: 'web',
    };

    dataLayerPush(eventPayload);
  } catch (error) {
    console.error('Error occurred in signUpEvent:', error);
  }
};

export const signInEvent = async () => {
  const { user_id, first_name, last_name, company_name, company_id, email } =
    await getContactDetailsWithoutMobile();

  try {
    const eventPayload: SignInEvent = {
      event: DataLayerEventName.SIGN_IN,
      user_id: user_id ?? '',
      first_name,
      last_name,
      company_name: company_name ?? '',
      company_id: company_id ?? '',
      email,
      source: 'web',
    };

    dataLayerPush(eventPayload);
  } catch (error) {
    console.error('Error occurred in signInEvent:', error);
  }
};

export const marketingEmailOptIn = async (
  company_id?: number | string,
  user_id?: number | string,
  marketing_email_opt_in?: boolean,
) => {
  try {
    // Fetch contact details if user_id or company_id are missing
    const contactDetails = !user_id || !company_id ? await getContactDetailsWithoutMobile() : null;

    // Fetch marketing email preference from pointcase if marketing_email_opt_in is not provided
    if (marketing_email_opt_in === undefined) {
      const messageSettings = await getAllMessageSettings(
        Number(user_id ?? contactDetails?.user_id),
      );
      marketing_email_opt_in =
        messageSettings
          .find((ms) => ms.message_type_name == MessageTypeGroups.MARKETING_NOTIFICATIONS)
          ?.channels.find((c) => c.channel_name.toLocaleLowerCase() == MessageTypes.EMAIL)
          ?.is_active || false;
    }

    const eventPayload: MarketingEmailOptInEvent = {
      event: DataLayerEventName.MARKETINT_EMAIL_OPT_IN,
      user_id: String(user_id ?? contactDetails?.user_id ?? ''),
      company_id: String(company_id ?? contactDetails?.company_id ?? ''),
      marketing_email_opt_in: Boolean(marketing_email_opt_in),
    };

    dataLayerPush(eventPayload);
  } catch (error) {
    console.error('Error occurred in marketingEmailOptIn:', error);
  }
};
